import { tryAuthorizeProject } from '@ff-os/pdp-shared-redux/apis/authorization/tryAuthorizeProject';
import { uploadPrivateAsset } from '@ff-os/pdp-shared-redux/apis/private-assets/uploadPrivateAsset';
import { blobUrlStore } from '@ff-os/rendering-react/asset-loading/blobUrlStore';
import { isSupportedByBrowser } from '@ff-os/shared-react/dom/images/image-browser-support';
import { normalizeImage } from '@ff-os/shared-react/dom/images/normalize-image';
import { PrivateAssetUploadAction } from '@ff-os/shared-utils/types/private-assets/PrivateAssetUploadAction';
import { selectAllSizes } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { selectVariantById } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { actions as generalUiActions, selectHasFotofunAutoOpenedThisSession, selectIsFotofunPanelOpen } from '@ff-scene-plugins/photo-prints-redux/slices/generalUiSlice';
import { actions as groupActions, selectGroupById } from '@ff-scene-plugins/photo-prints-redux/slices/groupsSlice';
import { selectAllPhotos } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpThunk } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsUploadContext } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsUploadContext';
import cuid from 'cuid';
import { detectFacesIn } from '../helpers/detectFacesIn';
import { incrementWithScheduledReset } from './withScheduledReset';

export const uploadImages = (images: File[], groupId: string): PhotoPrintsPdpThunk => {
    return async (dispatch, getState) => {

        // Check if we should auto-open FotoFun panel on first photo upload
        const stateBefore = getState();
        const photosBeforeUpload = selectAllPhotos(stateBefore);
        const hasAutoOpened = selectHasFotofunAutoOpenedThisSession(stateBefore);
        const isPanelOpen = selectIsFotofunPanelOpen(stateBefore);

        dispatch(groupActions.queuePhotos({ groupId, amount: images.length }));

        const sceneId = getState().scene.id;

        // Auto-open FotoFun panel when adding first photos
        if (photosBeforeUpload.length === 0 && !hasAutoOpened && !isPanelOpen && sceneId === "ecdcdb3f8cc3413a8530f8d383e9c376") {
            dispatch(generalUiActions.updateItem({ key: 'isFotofunPanelOpen', value: true }));
            dispatch(generalUiActions.markFotofunAutoOpenedThisSession());
        }

        await tryAuthorizeProject({ dispatch, getState });

        const step = async (index: number) => {

            const nextStep = () => {
                if (index + 1 < images.length) {
                    setTimeout(() => step(index + 1), 0); // let browser repaint
                }
            };

            const image = images[index];
            await uploadImage(image, groupId, dispatch, getState);

            nextStep();
        };

        await step(0);
    };
};

const uploadImage = async (image: File, groupId: string, dispatch: PhotoPrintsPdpDispatch, getState: () => any) => {

    // Get size information for the group to apply forced aspect ratio
    const state = getState();
    const group = selectGroupById(state, groupId);
    const variant = group ? selectVariantById(state, group.variantId) : null;
    const sizes = selectAllSizes(state);
    const size = variant && sizes ? sizes.find(s => s.id === variant.sizeId) : null;
    const hasBorder = variant?.borderId ? true : false;

    const magicBorderId = group?.magicBorderId;
    const patternId = group?.patternId;

    const request: PrivateAssetUploadAction<PhotoPrintsUploadContext> = {
        upload: { type: 'Photo', file: image },
        context: {
            id: cuid(),
            assetId: cuid(),
            groupId: groupId,
            processed: false,
            fileName: image.name,
            detections: [],
            sizeConfig: size || undefined,
            hasBorder: hasBorder,
            magicBorderId: magicBorderId || patternId,
            magicBorderRngSeed: magicBorderId || patternId ? `${cuid()}-${Date.now()}` : undefined
        }
    };

    parseAndUpload(image, request, dispatch)
        .then(() => dispatch(incrementWithScheduledReset(groupId, 'successfulUploadsCount')))
        .catch(() => dispatch(incrementWithScheduledReset(groupId, 'failedUploadsCount')));
};

const parseAndUpload = async (
    image: File,
    request: PrivateAssetUploadAction<PhotoPrintsUploadContext>,
    dispatch: PhotoPrintsPdpDispatch
) => {

    if (isSupportedByBrowser(image)) {

        const { url, width, height, capturedOn } = await normalizeImage(image, { readCapturedOn: true });

        const detections = await detectFacesIn(url, width, height);

        const photoUpload: PrivateAssetUploadAction<PhotoPrintsUploadContext> = {
            ...request,
            context: {
                ...request.context,
                processed: true,
                fileName: image.name,
                localUrl: url,
                imageWidth: width,
                imageHeight: height,
                capturedOn: capturedOn,
                autoFaceCenter: true,
                detections: detections
            }
        };

        blobUrlStore.track(url);
        return dispatch(uploadPrivateAsset(photoUpload)).unwrap();
    } else {
        return dispatch(uploadPrivateAsset(request)).unwrap();
    }
};